type LoginAuthRecheckGateState =
  | { readonly status: "authenticated" }
  | { readonly status: "requires-auth"; readonly errorMessage?: string };

export type LoginAuthRecheckOutcome =
  | { readonly kind: "authenticated" }
  | { readonly kind: "still-requires-auth"; readonly errorMessage?: string };

/**
 * A recheck that lands on a *rejected* desktop session (mismatched CMMD
 * principal, desktop state unavailable) comes back as `requires-auth` carrying
 * the reason — `rejectSplitDesktopAuthSession` logs the session out and builds
 * that message. The login surface is already mounted at that point, so the
 * reason has to be handed back to the caller or the user sees the untouched
 * login form with no explanation.
 */
export function resolveLoginAuthRecheckOutcome(
  gateState: LoginAuthRecheckGateState,
): LoginAuthRecheckOutcome {
  if (gateState.status === "authenticated") {
    return { kind: "authenticated" };
  }
  return gateState.errorMessage === undefined
    ? { kind: "still-requires-auth" }
    : { kind: "still-requires-auth", errorMessage: gateState.errorMessage };
}

export function shouldRecheckLoginAuthSession(visibilityState: DocumentVisibilityState): boolean {
  return visibilityState === "visible";
}

export type LoginRecheckFailure = {
  readonly message: string;
  /** Bumped only when the message itself changes — see below. */
  readonly attempt: number;
};

/**
 * Seeds the failure state from the error the route context already handed the
 * login surface. Starting from `null` instead would treat the very first
 * recheck as new information even when it returns the message already on
 * screen, remounting the form under the user for no visible change.
 */
export function seedLoginRecheckFailure(
  gateErrorMessage: string | undefined,
): LoginRecheckFailure | null {
  return gateErrorMessage === undefined || gateErrorMessage.length === 0
    ? null
    : { message: gateErrorMessage, attempt: 0 };
}

/**
 * The login surface holds its error text in `useState` seeded from
 * `initialErrorMessage`, so a fresh message only reaches the user when the
 * surface remounts. `attempt` is the remount key, and it deliberately does not
 * move when the same failure repeats: rechecks fire on every focus and
 * pageshow, and remounting on each one would wipe a half-typed email while the
 * user reads the error.
 */
export function resolveNextLoginRecheckFailure(
  previous: LoginRecheckFailure | null,
  errorMessage: string | undefined,
): LoginRecheckFailure | null {
  if (errorMessage === undefined || errorMessage.length === 0) {
    return previous;
  }
  if (previous?.message === errorMessage) {
    return previous;
  }
  return { message: errorMessage, attempt: (previous?.attempt ?? 0) + 1 };
}
